/**
 * Outil « Multiples et nombres premiers » (Boîte à outils, 6e → Tle).
 *
 * Grille de 1 à 100 : les multiples d'un entier s'allument, toucher une case
 * donne ses diviseurs (et les critères de divisibilité) ; crible
 * d'Ératosthène pas à pas. Dès la 3e : décomposition en facteurs premiers et
 * PGCD par l'algorithme d'Euclide, avec le rectangle découpé en carrés dont
 * le plus petit a pour côté le PGCD. Calculs : `arith.ts`.
 */

import { useState, type PointerEvent } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  NumberField,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
} from '../../ui/controls';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  divisors,
  euclid,
  euclidSquares,
  factorize,
  gcd,
  GRID_MAX,
  isPrime,
  commonFactors,
  powerAtoms,
  powerForm,
  sieve,
} from './arith';

type Mode = 'multiples' | 'sieve' | 'factor' | 'fraction' | 'gcd';

const CELL = 32;
const W = CELL * 10;
const SQUARE_COLORS = [
  'var(--color-primary-500)',
  'var(--color-accent-500)',
  'var(--color-success-500)',
  'var(--color-error-500)',
  'var(--color-ink-400)',
];

export function PrimesGridTool({ onSend, sendCost, level }: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  // décomposition et fraction irréductible : 4e ; PGCD et Euclide : Tle (maths expertes)
  const factorOk = rank === null || rank >= 3;
  const euclidOk = rank === null || rank >= 7;
  const powersOk = rank === null || rank >= 3;
  const [mode, setMode] = useState<Mode>('multiples');
  const [n, setN] = useState(7);
  const [picked, setPicked] = useState<number | null>(84);
  const [steps, setSteps] = useState(0);
  const [toFactor, setToFactor] = useState(84);
  const [a, setA] = useState(84);
  const [b, setB] = useState(36);
  const [num, setNum] = useState(84);
  const [den, setDen] = useState(36);

  const options: Array<{ value: Mode; label: string }> = [
    { value: 'multiples', label: 'Multiples' },
    { value: 'sieve', label: 'Crible' },
    ...(factorOk
      ? ([
          { value: 'factor', label: 'Décomposer' },
          { value: 'fraction', label: 'Fraction' },
        ] as const)
      : []),
    ...(euclidOk ? ([{ value: 'gcd', label: 'PGCD' }] as const) : []),
  ];

  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Mode" value={mode} options={options} onChange={setMode} />
      {mode === 'multiples' && (
        <MultiplesMode n={n} setN={setN} picked={picked} setPicked={setPicked} />
      )}
      {mode === 'sieve' && <SieveMode steps={steps} setSteps={setSteps} powers={powersOk} />}
      {mode === 'fraction' && (
        <FractionMode
          num={num}
          den={den}
          setNum={setNum}
          setDen={setDen}
          onSend={onSend}
          sendCost={sendCost}
        />
      )}
      {mode === 'factor' && (
        <FactorMode value={toFactor} setValue={setToFactor} onSend={onSend} sendCost={sendCost} />
      )}
      {mode === 'gcd' && (
        <GcdMode a={a} b={b} setA={setA} setB={setB} onSend={onSend} sendCost={sendCost} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grille 1 – 100

function Grid({
  cell,
  onPick,
  label,
  testId,
}: {
  cell: (k: number) => {
    fill: string;
    text: string;
    ring?: string;
    strike?: boolean;
    bold?: boolean;
  };
  onPick?: (k: number) => void;
  label: string;
  testId: string;
}) {
  const pick = (e: PointerEvent<SVGSVGElement>) => {
    if (!onPick) return;
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const col = Math.floor(((e.clientX - r.left) / r.width) * 10);
    const row = Math.floor(((e.clientY - r.top) / r.height) * 10);
    if (!Number.isFinite(col) || !Number.isFinite(row)) return;
    if (col < 0 || col > 9 || row < 0 || row > 9) return;
    onPick(row * 10 + col + 1);
  };
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${W}`}
        className={cn(
          'border-ink-100 block w-full select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        role="img"
        aria-label={label}
        data-testid={testId}
        onPointerDown={pick}
      >
        {Array.from({ length: GRID_MAX }, (_, i) => {
          const k = i + 1;
          const x = (i % 10) * CELL;
          const y = Math.floor(i / 10) * CELL;
          const c = cell(k);
          return (
            <g key={k}>
              <rect x={x + 1} y={y + 1} width={CELL - 2} height={CELL - 2} rx={5} fill={c.fill} />
              {c.ring && (
                <circle
                  cx={x + CELL / 2}
                  cy={y + CELL / 2}
                  r={CELL / 2 - 3}
                  fill="none"
                  stroke={c.ring}
                  strokeWidth={2}
                />
              )}
              <text
                x={x + CELL / 2}
                y={y + CELL / 2 + 4.5}
                textAnchor="middle"
                fontSize={12}
                fontWeight={c.bold ? 700 : 500}
                fill={c.text}
              >
                {k}
              </text>
              {c.strike && (
                <line
                  x1={x + 6}
                  y1={y + CELL - 7}
                  x2={x + CELL - 6}
                  y2={y + 7}
                  stroke="var(--color-ink-400)"
                  strokeWidth={1.5}
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

const PLAIN = { fill: 'var(--color-ink-50)', text: 'var(--color-ink-700)' };

function MultiplesMode({
  n,
  setN,
  picked,
  setPicked,
}: {
  n: number;
  setN: (v: number) => void;
  picked: number | null;
  setPicked: (k: number) => void;
}) {
  const count = Math.floor(GRID_MAX / n);
  const multiples = Array.from({ length: count }, (_, i) => (i + 1) * n);
  const divs = picked ? divisors(picked) : [];
  const digitSum = picked
    ? String(picked)
        .split('')
        .reduce((s, c) => s + Number(c), 0)
    : 0;
  return (
    <>
      <Stepper label="Multiples de" value={n} min={2} max={20} onChange={setN} />
      <Grid
        label={`Grille de 1 à 100 : les multiples de ${n} sont allumés.`}
        testId="pg-grid"
        onPick={setPicked}
        cell={(k) =>
          k % n === 0
            ? {
                fill: 'var(--color-primary-500)',
                text: '#fff',
                bold: true,
                ring: k === picked ? 'var(--color-ink-900)' : undefined,
              }
            : { ...PLAIN, ring: k === picked ? 'var(--color-ink-900)' : undefined }
        }
      />
      <p className="text-ink-800 text-sm" data-testid="pg-multiples">
        Multiples de {n} jusqu’à 100 :{' '}
        {multiples.length <= 12
          ? multiples.join(', ')
          : `${multiples.slice(0, 4).join(', ')}, …, ${multiples[multiples.length - 1]}`}{' '}
        ({count} multiples). Ce sont les nombres {n} × k pour k entier : ici k va de 1 à {count} (et
        0 = {n} × 0 est aussi un multiple de {n}).
      </p>
      <NumberField
        label="Case choisie"
        value={picked ?? 1}
        onChange={setPicked}
        min={1}
        max={GRID_MAX}
      />
      {picked !== null && (
        <section
          className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
          data-testid="pg-picked"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Diviseurs de {picked} : {divs.join(', ')} ({divs.length} diviseur
            {divs.length > 1 ? 's' : ''})
          </p>
          <p className="text-ink-700 text-sm">
            {picked === 1
              ? '1 n’est pas un nombre premier : il n’a qu’un seul diviseur.'
              : isPrime(picked)
                ? `${picked} est premier : exactement deux diviseurs, 1 et lui-même.`
                : `${picked} n’est pas premier.`}{' '}
            {picked % n === 0
              ? `${picked} est un multiple de ${n} (${picked} = ${n} × ${picked / n}).`
              : `${picked} n’est pas un multiple de ${n}.`}
          </p>
          <p className="text-ink-600 text-sm">
            Critères : dernier chiffre {picked % 10} →{' '}
            {picked % 2 === 0 ? 'divisible' : 'pas divisible'} par 2,{' '}
            {picked % 5 === 0 ? 'divisible' : 'pas divisible'} par 5,{' '}
            {picked % 10 === 0 ? 'divisible' : 'pas divisible'} par 10 ; somme des chiffres{' '}
            {digitSum} → {digitSum % 3 === 0 ? 'divisible' : 'pas divisible'} par 3,{' '}
            {digitSum % 9 === 0 ? 'divisible' : 'pas divisible'} par 9.
          </p>
        </section>
      )}
    </>
  );
}

function SieveMode({
  steps,
  setSteps,
  powers,
}: {
  steps: number;
  setSteps: (v: number) => void;
  /** écrire 5² (dès la 4e) ou 5 × 5 */
  powers: boolean;
}) {
  const sq = (p: number) => (powers ? `${p}²` : `${p} × ${p}`);
  const s = sieve(GRID_MAX, steps);
  const last = s.done[s.done.length - 1];
  const primes = Array.from({ length: GRID_MAX }, (_, i) => i + 1).filter(
    (k) => k >= 2 && !s.crossedBy[k],
  );
  return (
    <>
      <Grid
        label="Crible d’Ératosthène sur la grille de 1 à 100."
        testId="pg-sieve"
        cell={(k) => {
          if (k === 1) return { ...PLAIN, strike: true, text: 'var(--color-ink-400)' };
          if (s.crossedBy[k])
            return { fill: 'var(--color-ink-100)', text: 'var(--color-ink-400)', strike: true };
          if (s.done.includes(k) || s.next === null)
            return {
              fill: 'var(--color-success-50)',
              text: 'var(--color-ink-900)',
              ring: 'var(--color-success-500)',
              bold: true,
            };
          if (k === s.next) return { fill: 'var(--color-primary-500)', text: '#fff', bold: true };
          return PLAIN;
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={cn(PRIMARY_BUTTON, 'disabled:opacity-40')}
          disabled={s.next === null}
          onClick={() => setSteps(steps + 1)}
        >
          Étape suivante
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          disabled={s.next === null}
          onClick={() => setSteps(99)}
        >
          Tout cribler
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          disabled={steps === 0}
          onClick={() => setSteps(0)}
        >
          Recommencer
        </button>
      </div>
      <Note live>
        <span data-testid="pg-sieve-text">
          {steps === 0
            ? '1 n’est pas premier. 2 est premier : à l’étape suivante, on barre tous ses multiples (sauf 2).'
            : s.next !== null
              ? `On a barré les multiples de ${s.done.join(', ')}. Le plus petit nombre non barré après ${last}, ${s.next}, est premier : on barrera ses multiples à partir de ${sq(s.next)} = ${s.next * s.next}.`
              : `Fini : après ${last}, le premier suivant est 11 et ${sq(11)} = 121 > 100, plus rien à barrer. Les ${primes.length} nombres non barrés sont les nombres premiers jusqu’à 100.`}
        </span>
      </Note>
    </>
  );
}

function FactorMode({
  value,
  setValue,
  onSend,
  sendCost,
}: {
  value: number;
  setValue: (v: number) => void;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const { factors, steps } = factorize(value);
  const form = powerForm(factors);
  const atoms = powerAtoms(factors);
  return (
    <>
      <NumberField
        label="Nombre à décomposer"
        value={value}
        onChange={setValue}
        min={2}
        max={9999}
      />
      <ol
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2 font-mono text-sm"
        data-testid="pg-factor-steps"
        aria-label="Divisions successives"
      >
        {steps.map(([m, d, r], i) => (
          <li key={i} className="text-ink-800" style={{ paddingLeft: `${Math.min(i, 6) * 12}px` }}>
            {m} = {d} × {r}
          </li>
        ))}
        {steps.length === 0 && (
          <li className="text-ink-800">{value} est premier : sa décomposition, c’est lui-même.</li>
        )}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-ink-900 min-w-0 flex-1 text-base font-bold" data-testid="pg-factor">
          {value} = {form}
        </p>
        {onSend && (
          <SendToSheetButton label={form} cost={sendCost} onClick={() => onSend({ atoms })} />
        )}
      </div>
      <p className="text-ink-600 text-sm">
        On divise par le plus petit nombre premier possible, encore et encore. La décomposition est
        unique (à l’ordre près).
      </p>
    </>
  );
}

function GcdMode({
  a,
  b,
  setA,
  setB,
  onSend,
  sendCost,
}: {
  a: number;
  b: number;
  setA: (v: number) => void;
  setB: (v: number) => void;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const steps = euclid(a, b);
  const g = gcd(a, b);
  const squares = euclidSquares(a, b);
  const long = Math.max(a, b);
  const short = Math.min(a, b);
  const scale = Math.min(300 / long, 200 / short);
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="a" value={a} onChange={setA} min={1} max={999} />
        <NumberField label="b" value={b} onChange={setB} min={1} max={999} />
      </div>
      <ol
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2 font-mono text-sm"
        data-testid="pg-euclid"
        aria-label="Algorithme d’Euclide"
      >
        {steps.map((s, i) => (
          <li key={i} className={cn('text-ink-800', s.r === 0 && 'font-bold')}>
            {s.a} = {s.b} × {s.q} + {s.r}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-ink-900 min-w-0 flex-1 text-base font-bold" data-testid="pg-gcd">
          PGCD({a} ; {b}) = {g} — le diviseur de la dernière division, celle dont le reste est nul
          (c’est le dernier reste non nul).
        </p>
        {onSend && (
          <SendToSheetButton
            label={String(g)}
            cost={sendCost}
            onClick={() => onSend({ atoms: [String(g)] })}
          />
        )}
      </div>
      <p className="text-ink-700 text-sm">
        {g === 1
          ? `${a} et ${b} sont premiers entre eux : ${a}/${b} est déjà irréductible.`
          : b / g === 1
            ? `${a}/${b} = ${a / g} (on divise par ${g}).`
            : `Fraction irréductible : ${a}/${b} = ${a / g}/${b / g} (on divise par ${g}).`}
      </p>
      {squares.length <= 40 && g * scale >= 4 && (
        <svg
          viewBox={`0 0 ${long * scale + 4} ${short * scale + 4}`}
          className="mx-auto block w-full max-w-[22rem]"
          role="img"
          aria-label={`Rectangle ${long} × ${short} découpé en carrés : le plus petit carré a pour côté ${g}.`}
          data-testid="pg-rect"
        >
          {squares.map((sq, i) => (
            <rect
              key={i}
              x={2 + sq.x * scale}
              y={2 + sq.y * scale}
              width={sq.side * scale}
              height={sq.side * scale}
              fill={SQUARE_COLORS[sq.step % SQUARE_COLORS.length]}
              fillOpacity={0.35}
              stroke="var(--color-ink-700)"
              strokeWidth={1}
            />
          ))}
        </svg>
      )}
      {squares.length <= 40 && g * scale >= 4 && (
        <p className="text-ink-600 text-sm">
          Le rectangle {long} × {short} se découpe en carrés : à chaque division, autant de carrés
          que le quotient. Le plus petit carré, qui pave tout le reste, a pour côté le PGCD.
        </p>
      )}
    </>
  );
}

function FractionMode({
  num,
  den,
  setNum,
  setDen,
  onSend,
  sendCost,
}: {
  num: number;
  den: number;
  setNum: (v: number) => void;
  setDen: (v: number) => void;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const fn = factorize(num).factors;
  const fd = factorize(den).factors;
  const common = commonFactors(fn, fd);
  const g = common.reduce((p, f) => p * f, 1);
  const [p, q] = [num / g, den / g];
  const form = (fs: readonly number[]) => (fs.length ? powerForm(fs) : '1');
  const atoms = q === 1 ? [String(p)] : [String(p), '÷', String(q)];
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Numérateur" value={num} onChange={setNum} min={1} max={999} />
        <NumberField label="Dénominateur" value={den} onChange={setDen} min={1} max={999} />
      </div>
      <div
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2 text-sm"
        data-testid="pg-fraction"
      >
        <p className="text-ink-800">
          {num} = {form(fn)} ; {den} = {form(fd)}
        </p>
        <p className="text-ink-800">
          {common.length
            ? `Facteurs communs : ${form(common)} = ${g}.`
            : 'Aucun facteur premier commun.'}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <p
          className="text-ink-900 min-w-0 flex-1 text-base font-bold"
          data-testid="pg-fraction-result"
        >
          {common.length === 0
            ? `${num}/${den} est déjà irréductible.`
            : q === 1
              ? `${num}/${den} = ${p}`
              : `${num}/${den} = ${p}/${q}, irréductible.`}
        </p>
        {onSend && (
          <SendToSheetButton
            label={q === 1 ? String(p) : `${p}/${q}`}
            cost={sendCost}
            onClick={() => onSend({ atoms })}
          />
        )}
      </div>
      <p className="text-ink-600 text-sm">
        On simplifie par tous les facteurs premiers communs au numérateur et au dénominateur.
      </p>
    </>
  );
}
